import { useEffect, useMemo, useState } from 'react';
import { listInvestorAudit, listInvestorOrganizations } from '../../api/investorApi';
import { DataTable } from '../../components/ui/DataTable';
import { ErrorBanner } from '../../components/ui/ErrorBanner';
import { FilterSelect } from '../../components/ui/FilterSelect';
import { Pagination } from '../../components/ui/Pagination';
import { formatDateTime } from '../../utils/format';
import { humanize } from './investorLabels';

export function InvestorAuditPage() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [organizationId, setOrganizationId] = useState('');
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    listInvestorOrganizations({ pageSize: 100 })
      .then((response) => {
        if (!cancelled) setOrganizations(response?.items || []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError('');
      try {
        const response = await listInvestorAudit({ page, pageSize, organizationId });
        if (cancelled) return;
        setRows(response?.items || []);
        setTotal(response?.meta?.total ?? 0);
      } catch (requestError) {
        if (!cancelled) setError(requestError.message || 'Unable to load the audit log.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [page, pageSize, organizationId, reloadToken]);

  const orgOptions = useMemo(
    () => [
      { value: '', label: 'All organizations' },
      ...organizations.map((org) => ({ value: org.id, label: org.name })),
    ],
    [organizations]
  );

  const columns = useMemo(
    () => [
      {
        key: 'created_at',
        header: 'When',
        render: (row) => <span className="whitespace-nowrap">{formatDateTime(row.created_at)}</span>,
      },
      {
        key: 'actor',
        header: 'Who',
        render: (row) => (
          <div className="min-w-0">
            <span className="block font-semibold text-eorig-ink">
              {row.actor_name || row.actor_email || (row.actor_user_id ? 'User' : 'System')}
            </span>
            <span className="block text-[11px] text-eorig-muted">{row.actor_org_name || '—'}</span>
          </div>
        ),
      },
      { key: 'action', header: 'Action', render: (row) => humanize(row.action) },
      {
        key: 'target',
        header: 'Target',
        render: (row) => (
          <span className="text-eorig-muted">
            {humanize(row.target_type)}
            {row.target_id ? (
              <span className="block truncate font-mono text-[10px]" title={row.target_id}>
                {row.target_id}
              </span>
            ) : null}
          </span>
        ),
      },
      { key: 'message', header: 'Details', render: (row) => row.message || '—' },
    ],
    []
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <FilterSelect
          label="Organization"
          ariaLabel="Filter by acting organization"
          value={organizationId}
          onChange={(value) => {
            setPage(1);
            setOrganizationId(value);
          }}
          options={orgOptions}
        />
      </div>

      {error ? <ErrorBanner message={error} onRetry={() => setReloadToken((v) => v + 1)} /> : null}

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        loading={loading}
        emptyMessage="No administrative actions recorded."
        minWidth={880}
      />
      <Pagination
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPage(1);
          setPageSize(size);
        }}
        disabled={loading}
      />
    </div>
  );
}
